import React from 'react';
import { Search, Database, ShieldCheck, Sparkles } from 'lucide-react';

const RagTechnology = ({ lang }) => {
  const content = {
    en: {
      label: 'AI TECHNOLOGY',
      title: 'Powered by RAG: Zero Hallucinations, 100% Traceability',
      subtitle: 'Generic AI guesses. EQTOS verifies. Our Retrieval-Augmented Generation engine connects to live official sources for audit-ready accuracy.',
      steps: [
        {
          icon: Search,
          number: '01',
          title: 'Smart Retrieval',
          description: "When you ask, EQTOS doesn't just 'think'. It instantly queries live customs databases, HS codes, and trade agreements."
        },
        {
          icon: Database,
          number: '02',
          title: 'Source Verification',
          description: 'We cross-reference data against official government documents and real-time logistics constraints.'
        },
        {
          icon: ShieldCheck,
          number: '03',
          title: 'Trusted Response',
          description: 'You get a precise answer cited with sources, ensuring compliance and reducing risk.'
        }
      ],
      badge: 'RAG-POWERED',
      footer: 'Every response is traceable. Every decision is backed by data.'
    },
    es: {
      label: 'TECNOLOGÍA IA',
      title: 'Impulsado por RAG: Cero Alucinaciones, 100% Trazabilidad',
      subtitle: 'La IA genérica adivina. EQTOS verifica. Nuestro motor de Generación Aumentada por Recuperación se conecta a fuentes oficiales en vivo para precisión auditable.',
      steps: [
        {
          icon: Search,
          number: '01',
          title: 'Recuperación Inteligente',
          description: "Cuando preguntas, EQTOS no solo 'piensa'. Consulta instantáneamente bases de datos aduaneras en vivo, códigos HS y acuerdos comerciales."
        },
        {
          icon: Database,
          number: '02',
          title: 'Verificación de Fuentes',
          description: 'Cruzamos datos contra documentos gubernamentales oficiales y restricciones logísticas en tiempo real.'
        },
        {
          icon: ShieldCheck,
          number: '03',
          title: 'Respuesta Confiable',
          description: 'Obtienes una respuesta precisa citada con fuentes, asegurando cumplimiento y reduciendo riesgo.'
        }
      ],
      badge: 'POTENCIADO POR RAG',
      footer: 'Cada respuesta es rastreable. Cada decisión está respaldada por datos.'
    }
  };

  const t = content[lang] || content.en;

  return (
    <section className="py-16 lg:py-24 bg-black border-t border-[#00FFD1]/10 relative w-full overflow-hidden">
      {/* Background gradient */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[400px] bg-[#00FFD1]/5 rounded-full blur-[150px]" />
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-16 relative z-10">
        {/* Header */}
        <div className="text-center mb-12 lg:mb-16">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 sm:px-4 sm:py-2 border border-[#00FFD1]/40 bg-[#00FFD1]/5 mb-4 sm:mb-6">
            <Sparkles className="w-3 h-3 sm:w-4 sm:h-4 text-[#00FFD1]" />
            <span className="text-[#00FFD1] text-[10px] sm:text-xs font-semibold tracking-[0.15em] sm:tracking-[0.2em]">{t.label}</span>
          </div>
          
          <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-4 sm:mb-6 px-2 leading-tight">
            {t.title}
          </h2>
          
          <p className="text-sm sm:text-base md:text-lg text-white/60 max-w-3xl mx-auto leading-relaxed px-2">
            {t.subtitle}
          </p>
        </div>

        {/* 3-Step Flow */}
        <div className="relative">
          {/* Connection line - desktop only */}
          <div className="hidden lg:block absolute top-1/2 left-0 right-0 h-[2px] bg-gradient-to-r from-transparent via-[#00FFD1]/30 to-transparent -translate-y-1/2 z-0" />
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8 relative z-10">
            {t.steps.map((step, idx) => (
              <div 
                key={idx}
                className="relative group"
              >
                {/* Card */}
                <div className="bg-[#0a0a0a] border border-white/10 hover:border-[#00FFD1]/40 transition-all duration-500 p-6 sm:p-8 h-full">
                  {/* Step Number */}
                  <div className="absolute -top-3 -right-3 sm:-top-4 sm:-right-4">
                    <div className="w-10 h-10 sm:w-12 sm:h-12 bg-[#00FFD1] flex items-center justify-center">
                      <span className="text-black font-bold text-sm sm:text-base">{step.number}</span>
                    </div>
                  </div>

                  {/* Icon */}
                  <div className="w-14 h-14 sm:w-16 sm:h-16 flex items-center justify-center bg-[#00FFD1]/10 border border-[#00FFD1]/30 mb-6 group-hover:bg-[#00FFD1]/20 group-hover:border-[#00FFD1]/50 transition-all duration-300">
                    <step.icon className="w-7 h-7 sm:w-8 sm:h-8 text-[#00FFD1]" />
                  </div>

                  {/* Content */}
                  <h3 className="text-lg sm:text-xl font-bold text-white mb-3">
                    {step.title}
                  </h3>
                  
                  <p className="text-sm sm:text-base text-white/60 leading-relaxed">
                    {step.description}
                  </p>
                </div>

                {/* Arrow connector - mobile */}
                {idx < 2 && (
                  <div className="md:hidden flex justify-center my-4">
                    <div className="w-px h-8 bg-gradient-to-b from-[#00FFD1]/50 to-transparent" />
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Footer Badge */}
        <div className="mt-12 lg:mt-16 text-center">
          <div className="inline-flex flex-col sm:flex-row items-center gap-3 sm:gap-4 px-6 py-4 border border-[#00FFD1]/20 bg-[#00FFD1]/5">
            <div className="flex items-center gap-2">
              <ShieldCheck className="w-5 h-5 text-[#00FFD1]" />
              <span className="text-[#00FFD1] text-xs sm:text-sm font-semibold tracking-wider">{t.badge}</span>
            </div>
            <div className="hidden sm:block w-px h-4 bg-[#00FFD1]/30" />
            <span className="text-white/60 text-xs sm:text-sm text-center sm:text-left">
              {t.footer}
            </span>
          </div>
        </div>
      </div>
    </section>
  );
};

export default RagTechnology;
